import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { chartElementAtEvent } from './chartInteraction';
import { ArrowPathIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Chart as ChartJS } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import type { KpiData, KpiResilienceData } from './types';
import type { KpiDrilldownSpec } from './kpiDashboardApi';
import { SectionLabel } from './KpiDashboardContent';
import KpiChartPanel from './charts/KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from './chartFormatters';
import { KPI_BAR_SEQUENCE, KPI_LIGHT_BLUE } from './charts/kpiChartPalette';
import {
  resilienceCrossChipLabel,
  type KpiResilienceCrossSlice,
} from './kpiResilienceCrossFilter';
import { formatKpiRatePercentFromNumberString, formatKpiTwoDecimalDisplay } from './kpiDashboardNumberFormat';
import {
  KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL,
  KPI_DASHBOARD_TABLE_TH_STICKY_TOP,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';
const INCIDENT_LINE = '#1e293b';
const REQUEST_LINE = KPI_LIGHT_BLUE;

/** Fixed panel height for heatmap + priority (top row); scroll inside heatmap when needed. */
const RESILIENCE_TOP_PANEL_CLASS =
  'flex h-[400px] max-h-[400px] flex-col overflow-hidden sm:h-[400px]';
const RESILIENCE_PRIORITY_CHART_H_PX = 312;
const SLICE_DIM = 'rgba(148, 163, 184, 0.42)';

export interface KpiResilienceDashboardTabProps {
  chartsData: KpiData | undefined;
  chartsLoading: boolean;
  chartsFetching: boolean;
  chartsError: Error | null;
  chartsOnRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
  crossFilter: KpiResilienceCrossSlice;
  onCrossFilterChange: (next: KpiResilienceCrossSlice) => void;
}

function maxMttrInHeatmap(data: KpiResilienceData['mttrHeatmap']): number {
  let m = 0;
  for (const row of data.mttrByWeekAndWeekday) {
    for (const v of row) {
      if (v != null && Number.isFinite(v) && v > m) m = v;
    }
  }
  return m > 0 ? m : 1;
}

function cellStyle(mttr: number | null, maxMttr: number): { backgroundColor: string; color: string } {
  if (mttr == null || !Number.isFinite(mttr)) {
    return { backgroundColor: '#f1f5f9', color: '#94a3b8' };
  }
  const t = Math.min(1, mttr / maxMttr);
  const lightness = 94 - t * 46;
  const saturation = 28 + t * 52;
  const bg = `hsl(217, ${saturation}%, ${lightness}%)`;
  const color = t > 0.52 ? '#ffffff' : '#0f172a';
  return { backgroundColor: bg, color };
}

/** Non-selected cells while a heatmap slice is active — values stay visible, read as disabled. */
const HEATMAP_CELL_DISABLED: { backgroundColor: string; color: string } = {
  backgroundColor: '#f1f5f9',
  color: '#94a3b8',
};
const HEATMAP_CELL_DISABLED_EMPTY: { backgroundColor: string; color: string } = {
  backgroundColor: '#f8fafc',
  color: '#cbd5e1',
};

function ResilienceKpiHeaderStrip({
  mttrOverall,
  mttrIncidents,
  mttrRequests,
  changeFailurePct,
  onMetric,
}: {
  mttrOverall: string;
  mttrIncidents: string;
  mttrRequests: string;
  changeFailurePct: string;
  onMetric: (key: string) => void;
}) {
  const items = [
    { label: 'MTTR (Minutes)', value: formatKpiTwoDecimalDisplay(mttrOverall), drillKey: 'mttr' as const },
    { label: 'MTTR (Incidents)', value: formatKpiTwoDecimalDisplay(mttrIncidents), drillKey: 'incidents_mttr' as const },
    { label: 'MTTR (Requests)', value: formatKpiTwoDecimalDisplay(mttrRequests), drillKey: 'requests_mttr' as const },
    {
      label: 'Change Failure Rate',
      value: formatKpiRatePercentFromNumberString(
        changeFailurePct.includes('%') ? changeFailurePct : `${changeFailurePct}%`
      ),
      drillKey: 'change_failure' as const,
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
      <div
        className="h-1 bg-gradient-to-r from-[#2F5597] via-[#4472C4] to-sky-500"
        aria-hidden
      />
      <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-y-0">
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => onMetric(item.drillKey)}
            className="flex flex-col items-start px-5 py-4 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4472C4] sm:px-6 sm:py-5"
          >
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
            <span className="mt-1 text-2xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-3xl">
              {item.value}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MttrByWeekdayHeatmap({
  data,
  crossHeatmap,
  dimUnselected,
  onCellToggle,
  onCellDrill,
}: {
  data: KpiResilienceData['mttrHeatmap'];
  crossHeatmap: { weekStart: string; isoDow: number } | null;
  /** When true, non-selected cells are faded (active heatmap scope). */
  dimUnselected: boolean;
  onCellToggle: (payload: {
    weekStart: string;
    isoDow: number;
    weekLabel: string;
    weekdayLabel: string;
  }) => void;
  onCellDrill?: (weekStart: string, isoDow: number) => void;
}) {
  const maxM = useMemo(() => maxMttrInHeatmap(data), [data]);
  const { weekLabels, weekStarts, weekdayLabels, mttrByWeekAndWeekday, countsByWeekAndWeekday } = data;

  if (!weekLabels.length) {
    return (
      <p className="px-4 py-10 text-center text-sm text-slate-500">
        No week range or incident/request filters exclude both types — adjust filters to see MTTR by day of week.
      </p>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
      <table className="w-full min-w-[520px] border-collapse text-center text-xs">
        <thead>
          <tr>
            <th
              className={`${KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL} min-w-[9rem] text-center`}
              scope="col"
            >
              Week
            </th>
            {weekdayLabels.map((d) => (
              <th key={d} className={KPI_DASHBOARD_TABLE_TH_STICKY_TOP} scope="col">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weekLabels.map((wl, ri) => (
            <tr key={wl} className={`border-b border-slate-200 ${kpiDashboardTableRowClass(ri)}`}>
              <th
                className="sticky left-0 z-10 border border-slate-200 bg-inherit px-2 py-2 text-left text-[11px] font-semibold text-slate-800"
                scope="row"
              >
                {wl}
              </th>
              {weekdayLabels.map((wdLabel, ci) => {
                const mttr = mttrByWeekAndWeekday[ri]?.[ci] ?? null;
                const cnt = countsByWeekAndWeekday[ri]?.[ci] ?? 0;
                const style = cellStyle(mttr, maxM);
                const label =
                  mttr != null && Number.isFinite(mttr)
                    ? `${mttr.toFixed(1)} min${cnt > 0 ? ` · n=${cnt}` : ''}`
                    : '—';
                const weekStart = weekStarts[ri] ?? '';
                const isoDow = ci + 1;
                const hasData = cnt > 0 && mttr != null && Number.isFinite(mttr) && weekStart.length > 0;
                const selected =
                  Boolean(crossHeatmap) &&
                  crossHeatmap!.weekStart === weekStart &&
                  crossHeatmap!.isoDow === isoDow;
                const scoped = Boolean(dimUnselected && crossHeatmap);
                const visualStyle =
                  scoped && !selected
                    ? hasData
                      ? HEATMAP_CELL_DISABLED
                      : HEATMAP_CELL_DISABLED_EMPTY
                    : style;
                const fontClass =
                  scoped && !selected && hasData
                    ? 'font-normal'
                    : scoped && selected && hasData
                      ? 'font-semibold'
                      : 'font-semibold';
                return (
                  <td
                    key={`${ri}-${ci}`}
                    className={`min-w-[3.25rem] border border-slate-200 px-0.5 py-1.5 tabular-nums ${fontClass} ${
                      hasData ? 'cursor-pointer transition-colors hover:brightness-[0.98]' : ''
                    } ${selected && hasData ? 'ring-2 ring-inset ring-sky-600/55' : ''}`}
                    style={visualStyle}
                    title={label + (hasData ? ' — Click to filter; click again to clear.' : '')}
                    role={hasData ? 'button' : undefined}
                    tabIndex={hasData ? 0 : undefined}
                    onClick={() => {
                      if (!hasData) return;
                      onCellToggle({
                        weekStart,
                        isoDow,
                        weekLabel: wl,
                        weekdayLabel: wdLabel,
                      });
                    }}
                    onDoubleClick={() => {
                      if (!hasData) return;
                      onCellDrill?.(weekStart, isoDow);
                    }}
                    onKeyDown={(e) => {
                      if (!hasData) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCellToggle({
                          weekStart,
                          isoDow,
                          weekLabel: wl,
                          weekdayLabel: wdLabel,
                        });
                      }
                    }}
                  >
                    {mttr != null && Number.isFinite(mttr) ? mttr.toFixed(0) : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const KpiResilienceDashboardTab: React.FC<KpiResilienceDashboardTabProps> = ({
  chartsData,
  chartsLoading,
  chartsFetching,
  chartsError,
  chartsOnRetry,
  onDrill,
  crossFilter,
  onCrossFilterChange,
}) => {
  const priorityBarRef = useRef<ChartJS<'bar'>>(null);
  const groupBarRef = useRef<ChartJS<'bar'>>(null);
  const trendRef = useRef<ChartJS<'line'>>(null);
  /** Full heatmap before a weekday slice — keeps other cells visible (gray) while scoped. */
  const heatmapBaselineRef = useRef<KpiResilienceData['mttrHeatmap'] | null>(null);

  useEffect(() => {
    if (!chartsData) return;
    if (crossFilter?.kind !== 'heatmap') {
      heatmapBaselineRef.current = chartsData.resilience.mttrHeatmap;
    }
  }, [chartsData, crossFilter]);

  const openMetric = useCallback(
    (key: string) => {
      if (key === 'mttr') onDrill({ kind: 'metric', key: 'mttr' });
      else if (key === 'incidents_mttr') onDrill({ kind: 'metric', key: 'incidents_mttr' });
      else if (key === 'requests_mttr') onDrill({ kind: 'metric', key: 'requests_mttr' });
      else if (key === 'change_failure') onDrill({ kind: 'metric', key: 'change_failure' });
    },
    [onDrill]
  );

  const handleHeatmapToggle = useCallback(
    (payload: {
      weekStart: string;
      isoDow: number;
      weekLabel: string;
      weekdayLabel: string;
    }) => {
      if (
        crossFilter?.kind === 'heatmap' &&
        crossFilter.weekStart === payload.weekStart &&
        crossFilter.isoDow === payload.isoDow
      ) {
        onCrossFilterChange(null);
        return;
      }
      onCrossFilterChange({
        kind: 'heatmap',
        weekStart: payload.weekStart,
        isoDow: payload.isoDow,
        weekLabel: payload.weekLabel,
        weekdayLabel: payload.weekdayLabel,
      });
    },
    [crossFilter, onCrossFilterChange]
  );

  const handlePriorityBarClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = priorityBarRef.current;
      if (!chart) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const idx = el.index;
      const row = chartsData?.resilience.mttrByPriority[idx];
      if (!row || row.count <= 0) return;
      const label = row.priorityLabel;
      if (crossFilter?.kind === 'priority' && crossFilter.label === label) {
        onCrossFilterChange(null);
      } else {
        onCrossFilterChange({ kind: 'priority', label });
      }
    },
    [chartsData?.resilience.mttrByPriority, crossFilter, onCrossFilterChange]
  );

  const handlePriorityValueDrill = useCallback(
    (priorityLabel: string) => {
      onDrill({ kind: 'resilience_priority', priorityLabel });
    },
    [onDrill]
  );

  const handleHeatmapCellDrill = useCallback(
    (weekStart: string, isoDow: number) => {
      onDrill({ kind: 'resilience_heatmap_cell', weekStart, isoDow });
    },
    [onDrill]
  );

  const handleTrendWeekDrill = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = trendRef.current;
      if (!chart) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const idx = el.index;
      const row = chartsData?.resilience.mttrTrendByWeek[idx];
      if (!row) return;
      const series = el.datasetIndex === 0 ? 'INCIDENT' : 'SERVICE_REQUEST';
      onDrill({ kind: 'resilience_trend_week', weekStart: row.weekStart, series });
    },
    [chartsData?.resilience.mttrTrendByWeek, onDrill]
  );

  const handleGroupBarClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chart = groupBarRef.current;
      if (!chart) return;
      const els = chartElementAtEvent(chart, event);
      const el = els[0];
      if (el == null) return;
      const idx = el.index;
      const row = chartsData?.resilience.mttrByAssignmentGroup[idx];
      if (!row || row.count <= 0) return;
      const groupName = row.groupName;
      if (crossFilter?.kind === 'group' && crossFilter.groupName === groupName) {
        onCrossFilterChange(null);
      } else {
        onCrossFilterChange({ kind: 'group', groupName });
      }
    },
    [chartsData?.resilience.mttrByAssignmentGroup, crossFilter, onCrossFilterChange]
  );

  const handleGroupValueDrill = useCallback(
    (groupName: string) => {
      onDrill({ kind: 'resilience_assignment_group', groupName });
    },
    [onDrill]
  );

  if (chartsLoading && !chartsData) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading resilience dashboard">
        <div className="h-28 animate-pulse rounded-xl bg-slate-200/80" />
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-80 animate-pulse rounded-xl bg-slate-200/80" />
          <div className="h-80 animate-pulse rounded-xl bg-slate-200/80" />
          <div className="h-80 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-2" />
          <div className="h-80 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-2" />
        </div>
      </div>
    );
  }

  const blockingError = chartsError && !chartsData;
  if (blockingError) {
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load resilience dashboard</p>
        <p className="mt-1 text-sm text-red-700/90">{chartsError?.message}</p>
        {chartsOnRetry && (
          <button
            type="button"
            onClick={() => chartsOnRetry()}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!chartsData) return null;

  const resilience = chartsData.resilience;
  const heatmapForTable =
    crossFilter?.kind === 'heatmap' && heatmapBaselineRef.current
      ? heatmapBaselineRef.current
      : resilience.mttrHeatmap;
  const mttrOverall = chartsData.mttrMinutes !== '—' ? chartsData.mttrMinutes : '—';
  const mttrInc =
    chartsData.incidentsMttrMinutes !== '—' ? chartsData.incidentsMttrMinutes : chartsData.mttrMinutes;
  const mttrReq =
    chartsData.requestsMttrMinutes !== '—' ? chartsData.requestsMttrMinutes : '—';

  const priorityLabels = resilience.mttrByPriority.map((r) => r.priorityLabel);
  const priorityValues = resilience.mttrByPriority.map((r) =>
    r.count > 0 && r.mttrMinutes != null && Number.isFinite(r.mttrMinutes) ? r.mttrMinutes : 0
  );
  const hasPriorityData = resilience.mttrByPriority.some((r) => r.count > 0);

  const trend = resilience.mttrTrendByWeek;
  const trendLabels = trend.map((w) => w.weekLabel);
  const incidentTrend = trend.map((w) => w.incidentMttrMinutes);
  const requestTrend = trend.map((w) => w.requestMttrMinutes);
  const trendNumeric = trend.flatMap((w) =>
    [w.incidentMttrMinutes, w.requestMttrMinutes].filter((x): x is number => x != null && Number.isFinite(x))
  );
  const trendMax = trendNumeric.length ? Math.max(...trendNumeric, 1) : 1;

  const groups = resilience.mttrByAssignmentGroup;
  const groupLabels = groups.map((g) => g.groupName);
  const groupValues = groups.map((g) => g.mttrMinutes ?? 0);
  const groupColors = groups.map((_, i) => KPI_BAR_SEQUENCE[i % KPI_BAR_SEQUENCE.length]);
  const hasGroupData = groupValues.some((v) => v > 0);

  const crossChip = resilienceCrossChipLabel(crossFilter);
  const basePriColors = priorityLabels.map((_, i) => KPI_BAR_SEQUENCE[i % KPI_BAR_SEQUENCE.length]);
  const priorityBarsBg =
    crossFilter?.kind === 'priority'
      ? basePriColors.map((c, i) => (priorityLabels[i] === crossFilter.label ? c : SLICE_DIM))
      : basePriColors;
  const groupBarsBg =
    crossFilter?.kind === 'group'
      ? groupColors.map((c, i) => (groupLabels[i] === crossFilter.groupName ? c : SLICE_DIM))
      : groupColors;

  return (
    <div className="relative">
      {chartsFetching && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {chartsError && chartsData && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">Showing cached data. Latest refresh failed: {chartsError.message}</span>
          <button
            type="button"
            onClick={() => chartsOnRetry?.()}
            className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
          >
            Retry
          </button>
        </div>
      )}

      <section className="mb-6">
        <SectionLabel>Resilience KPIs</SectionLabel>
        <ResilienceKpiHeaderStrip
          mttrOverall={mttrOverall}
          mttrIncidents={mttrInc}
          mttrRequests={mttrReq}
          changeFailurePct={chartsData.changeFailureRate}
          onMetric={openMetric}
        />
      </section>

      <section className="mb-6">
        <SectionLabel>MTTR analysis</SectionLabel>
        {/* {crossChip ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-950 shadow-sm">
              <span>Scope: {crossChip}</span>
              <button
                type="button"
                onClick={() => onCrossFilterChange(null)}
                className="inline-flex items-center rounded-full p-0.5 text-amber-800 hover:bg-amber-200/60 hover:text-amber-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                aria-label="Clear resilience scope filter"
              >
                <XMarkIcon className="h-4 w-4" aria-hidden />
              </button>
            </span>
          </div>
        ) : null} */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <KpiChartPanel
            title="MTTR By Day Of Week"
            titleClassName="text-center sm:text-center"
            className={RESILIENCE_TOP_PANEL_CLASS}
            bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden px-0 pb-0 pt-0"
          >
            <MttrByWeekdayHeatmap
              data={heatmapForTable}
              crossHeatmap={
                crossFilter?.kind === 'heatmap'
                  ? { weekStart: crossFilter.weekStart, isoDow: crossFilter.isoDow }
                  : null
              }
              dimUnselected={crossFilter?.kind === 'heatmap'}
              onCellToggle={handleHeatmapToggle}
              onCellDrill={handleHeatmapCellDrill}
            />
          </KpiChartPanel>

          <KpiChartPanel
            title="MTTR By Priority"
            titleClassName="text-center"
            className={RESILIENCE_TOP_PANEL_CLASS}
            bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden px-0 pb-2 pt-0"
          >
            <div
              className="relative w-full shrink-0 px-1"
              style={{ height: RESILIENCE_PRIORITY_CHART_H_PX, maxHeight: RESILIENCE_PRIORITY_CHART_H_PX }}
            >
              {!hasPriorityData ? (
                <p className="px-3 py-10 text-center text-sm text-slate-500">
                  No resolved incident or request tickets in range for priority breakdown.
                </p>
              ) : (
                <Bar
                  ref={priorityBarRef}
                  onClick={handlePriorityBarClick}
                  data={{
                    labels: priorityLabels,
                    datasets: [
                      {
                        label: 'MTTR (minutes)',
                        data: priorityValues,
                        backgroundColor: priorityBarsBg,
                        borderRadius: 6,
                        maxBarThickness: 32,
                        datalabels: {
                          display: true,
                          anchor: 'end',
                          align: 'end',
                          offset: 4,
                          color: '#111827',
                          font: { weight: 600, size: 10 },
                          formatter: (value: number) =>
                            value > 0 ? formatDataLabelCompact(Number(value)) : '',
                          listeners: {
                            click: (ctx: any, ev: any) => {
                              const idx = ctx.dataIndex as number;
                              const row = resilience.mttrByPriority[idx];
                              if (!row || row.count <= 0) return false;
                              handlePriorityValueDrill(row.priorityLabel);
                              ev?.native?.stopPropagation?.();
                              return true;
                            },
                          } as any,
                        },
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 18, right: 8, bottom: 4 } },
                    interaction: { mode: 'nearest', intersect: true },
                    onHover: (event, elements, chart) => {
                      const canvas = chart.canvas;
                      const el = elements[0];
                      if (el != null) {
                        const row = resilience.mttrByPriority[el.index];
                        canvas.style.cursor = row && row.count > 0 ? 'pointer' : 'default';
                      } else {
                        canvas.style.cursor = 'default';
                      }
                    },
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: { color: '#64748b', font: { size: 11, weight: 600 } },
                      },
                      y: {
                        beginAtZero: true,
                        suggestedMax: Math.max(...priorityValues, 1) * 1.12,
                        grid: { color: 'rgba(148, 163, 184, 0.35)' },
                        border: { display: false },
                        ticks: {
                          color: '#94a3b8',
                          callback: (v) => formatAxisCompactK(v as number),
                        },
                      },
                    },
                  }}
                />
              )}
            </div>
          </KpiChartPanel>

          <KpiChartPanel
            title="MTTR By Assignment Group"
            titleClassName="text-center"
            className="min-h-[22rem] lg:col-span-2 lg:min-h-[24rem]"
            flexFill
          >
            <div className="relative min-h-[280px] w-full flex-1 px-1">
              {!hasGroupData ? (
                <p className="px-3 py-10 text-center text-sm text-slate-500">
                  No resolved incident or request data by assignment group for this range.
                </p>
              ) : (
                <Bar
                  ref={groupBarRef}
                  onClick={handleGroupBarClick}
                  data={{
                    labels: groupLabels,
                    datasets: [
                      {
                        label: 'MTTR (minutes)',
                        data: groupValues,
                        backgroundColor: groupBarsBg,
                        borderRadius: 6,
                        barThickness: 28,
                        datalabels: {
                          display: true,
                          anchor: 'end',
                          align: 'end',
                          offset: 4,
                          color: '#111827',
                          font: { weight: 600, size: 10 },
                          formatter: (value: number) =>
                            value > 0 ? formatDataLabelCompact(Number(value)) : '',
                          listeners: {
                            click: (ctx: any, ev: any) => {
                              const idx = ctx.dataIndex as number;
                              const row = resilience.mttrByAssignmentGroup[idx];
                              if (!row || row.count <= 0) return false;
                              handleGroupValueDrill(row.groupName);
                              ev?.native?.stopPropagation?.();
                              return true;
                            },
                          } as any,
                        },
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'x',
                    layout: { padding: { top: 18, right: 8, bottom: 4 } },
                    interaction: { mode: 'nearest', intersect: true },
                    onHover: (event, elements, chart) => {
                      const canvas = chart.canvas;
                      const el = elements[0];
                      if (el != null) {
                        const row = resilience.mttrByAssignmentGroup[el.index];
                        canvas.style.cursor = row && row.count > 0 ? 'pointer' : 'default';
                      } else {
                        canvas.style.cursor = 'default';
                      }
                    },
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: {
                          color: '#64748b',
                          maxRotation: 55,
                          minRotation: 35,
                          font: { size: 10 },
                        },
                      },
                      y: {
                        beginAtZero: true,
                        suggestedMax: Math.max(...groupValues, 1) * 1.12,
                        grid: { color: 'rgba(148, 163, 184, 0.35)' },
                        border: { display: false },
                        ticks: {
                          color: '#94a3b8',
                          callback: (v) => formatAxisCompactK(v as number),
                        },
                      },
                    },
                  }}
                />
              )}
            </div>
          </KpiChartPanel>

          <KpiChartPanel
            title="MTTR Trend By Week"
            titleClassName="text-center"
            className="min-h-[22rem] lg:col-span-2 lg:min-h-[20rem]"
            flexFill
          >
            <div className="relative min-h-[280px] w-full flex-1 px-1">
              {trend.length === 0 ? (
                <p className="px-3 py-10 text-center text-sm text-slate-500">No weeks in selected date range.</p>
              ) : (
                <Line
                  ref={trendRef}
                  onClick={handleTrendWeekDrill}
                  data={{
                    labels: trendLabels,
                    datasets: [
                      {
                        label: 'Incident',
                        data: incidentTrend,
                        borderColor: INCIDENT_LINE,
                        backgroundColor: 'rgba(30, 41, 59, 0.06)',
                        fill: false,
                        tension: 0.35,
                        pointRadius: 3,
                        pointHoverRadius: 6,
                        pointBackgroundColor: '#fff',
                        pointBorderColor: INCIDENT_LINE,
                        pointBorderWidth: 2,
                        borderWidth: 2,
                        spanGaps: true,
                        datalabels: { display: false },
                      },
                      {
                        label: 'Request',
                        data: requestTrend,
                        borderColor: REQUEST_LINE,
                        backgroundColor: 'transparent',
                        fill: false,
                        tension: 0.35,
                        pointRadius: 3,
                        pointHoverRadius: 6,
                        pointBackgroundColor: '#fff',
                        pointBorderColor: REQUEST_LINE,
                        pointBorderWidth: 2,
                        borderWidth: 2,
                        spanGaps: true,
                        datalabels: { display: false },
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 12, right: 12, bottom: 4 } },
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: {
                        display: true,
                        position: 'top',
                        labels: { usePointStyle: true, boxWidth: 8, color: '#475569', font: { size: 11 } },
                      },
                    },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: { color: '#64748b', maxRotation: 45, autoSkip: true, maxTicksLimit: 24 },
                      },
                      y: {
                        beginAtZero: true,
                        suggestedMax: trendMax * 1.1,
                        grid: { color: 'rgba(148, 163, 184, 0.35)' },
                        border: { display: false },
                        ticks: {
                          color: '#94a3b8',
                          callback: (v) => formatAxisCompactK(v as number),
                        },
                      },
                    },
                  }}
                />
              )}
            </div>
          </KpiChartPanel>

        </div>
      </section>
    </div>
  );
};

export default KpiResilienceDashboardTab;
